import { ErrorMessage, TaAutocomplete, TaInput } from '@components/shared';
import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { useMapsStore } from '@components/shared/Maps/store';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { useGeolocated } from '@components/shared/Maps/useGeoLocated';
import { useCurrentAddress } from '@components/shared/Maps/utils';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import CustomTooltip from '@components/tooltip/tooltip';
import { FileUploadIndicator } from '@features/auth/registration/components/FileIndicator';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useModalDimensions } from '@hooks/useModalDimensions';
import { usePageTitle } from '@hooks/usePageTitle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { styled } from '@mui/material/styles';
import { useDeleteFarmFile, useFarmStore, useSingleFarm, useUpdateFarm } from '@pages/MyFarm/store';
import { Dictionary, Nullable } from '@utils/util.types';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useCrops } from 'services/crops';
import { useMultipartUpload, useUploadStore } from 'services/file-manager';
import { useIrrigationStore, useIrrigationTypes } from 'services/irrigations';
import * as Yup from 'yup';

interface CropInfo {
	crop_latitude: number;
	crop_longitude: number;
	crop_location_name: string;
	planted: boolean;
	weather_station: boolean;
	irrigation_water: boolean;
	irrigation_system: boolean;
	irrigation_system_id: Nullable<Dictionary>;
	sensors: boolean;
	management_soft: boolean;
	greenhouse: boolean;
	soil_analysis: boolean;
}

const validationSchema = Yup.object({
	crop_location_name: Yup.string(),
	crop_latitude: Yup.number(),
	crop_longitude: Yup.number(),
	planted: Yup.boolean(),
	weather_station: Yup.boolean(),
	irrigation_water: Yup.boolean(),
	irrigation_system: Yup.boolean(),
	irrigation_system_id: Yup.mixed().when('irrigation_system', {
		is: true,
		then: (schema) => schema.required('myFarm.irrigationSystemValidation'),
		otherwise: (schema) => schema.nullable(),
	}),
	sensors: Yup.boolean(),
	management_soft: Yup.boolean(),
	greenhouse: Yup.boolean(),
	soil_analysis: Yup.boolean(),
});

const initialValues: CropInfo = {
	crop_latitude: 0,
	crop_longitude: 0,
	crop_location_name: '',
	planted: false,
	weather_station: false,
	irrigation_water: false,
	irrigation_system: false,
	irrigation_system_id: null,
	sensors: false,
	soil_analysis: false,
	management_soft: false,
	greenhouse: false,
};

const VisuallyHiddenInput = styled('input')({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: 1,
	overflow: 'hidden',
	position: 'absolute',
	bottom: 0,
	left: 0,
	whiteSpace: 'nowrap',
	width: 1,
});

const checkboxFields: { label: string; name: keyof CropInfo }[] = [
	{ label: 'planted', name: 'planted' },
	{ label: 'weather_station', name: 'weather_station' },
	{ label: 'irrigation_water', name: 'irrigation_water' },
	{ label: 'irrigation_system', name: 'irrigation_system' },
	{ label: 'sensors', name: 'sensors' },
	{ label: 'management_soft', name: 'management_soft' },
	{ label: 'greenhouse', name: 'greenhouse' },
	{ label: 'soil_analysis', name: 'soil_analysis' },
];

export function FarmBasicsLocation() {
	usePageTitle(`My Farm`);
	const modalDimensions = useModalDimensions();
	const { t } = useTranslation();
	const { coords } = useGeolocated();
	const { address, loading } = useCurrentAddress();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { update } = useUpdateFarm();
	const { irrigation } = useIrrigationStore();
	const { id } = useParams();
	const localFarmId = localStorage.getItem('farmId') || '';
	const farmId = id || localFarmId;
	const [mapOpen, setMapOpen] = useState(false);
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const { uploadFiles, resetStatus, isAllCompleted } = useMultipartUpload();
	const { uploadStatus } = useUploadStore();
	const { setAddress } = useMapsStore();
	const openMapsModal = () => setMapOpen(true);
	const closeMapsModal = () => {
		setMapOpen(false);
	};
	const { singleFarm } = useFarmStore();
	const { remove } = useDeleteFarmFile();
	const { refresh } = useSingleFarm(farmId || '');
	const position = useMemo(
		() => ({
			lat: coords?.latitude || 0,
			lng: coords?.longitude || 0,
		}),
		[coords]
	);

	useIrrigationTypes();
	useCrops();

	const formik = useFormik<CropInfo>({
		initialValues,
		validationSchema,
		onSubmit: async (values) => {
			const formattedValues = {
				...values,
				irrigation_system_id: values.irrigation_system_id?.id,
				soil_analysis: undefined,
				farm_files: uploadStatus.map((file) => ({ file: file.key })),
			};

			try {
				await update(formattedValues as any, farmId as string);
				localStorage.removeItem('farmId');
				setAddress('');

				navigateWithLanguage('/farmer/my-farm');
				showSnackbar(
					t('myFarm.updateSnackbarTitle'),
					t('myFarm.updateSnackbarDescription'),
					'success'
				);
			} catch (error) {
				showSnackbar('Error', 'Something went wrong. Please try again.', 'error');
			}
		},
	});
	const { setValues, setFieldValue } = formik;

	useEffect(() => {
		setValues({
			crop_latitude: parseFloat(singleFarm?.crop_latitude) || (position?.lat as number),
			crop_longitude: parseFloat(singleFarm?.crop_longitude) || (position?.lng as number),
			crop_location_name: singleFarm?.crop_location_name || address,
			planted: singleFarm?.planted,
			weather_station: singleFarm?.weather_station,
			irrigation_water: singleFarm?.irrigation_water,
			irrigation_system: singleFarm?.irrigation_system,
			irrigation_system_id: singleFarm?.irrigationSystem || '',
			sensors: singleFarm?.sensors,
			management_soft: singleFarm?.management_soft,
			greenhouse: singleFarm?.greenhouse,
			soil_analysis: singleFarm?.farm_files?.length > 0,
		});
	}, [singleFarm, address, setFieldValue, position, setValues]);

	const handleLocationSelect = (address: string, lat: number, lng: number) => {
		setFieldValue('crop_location_name', address);
		setFieldValue('crop_latitude', lat);
		setFieldValue('crop_longitude', lng);
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(event.target.files || []);

		if (files.length > 0) {
			const result = await uploadFiles(files);

			if (result) {
				await update(
					{
						farm_files: result
							.filter((status) => status.isCompleted)
							.map((status) => ({ file: status.key })),
					},
					farmId as string
				);
				resetStatus();
				await refresh();
			}
			event.target.value = '';
		}
	};

	const handleFileRemove = async (fileId: string) => {
		await remove(fileId);
		await refresh();
		showSnackbar(t('fileRemoved'), t('fileRemovedSuccessfully'), 'success');
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = e.target;

		formik.handleChange(e);
		if (name === 'irrigation_system' && !checked) {
			formik.setFieldValue('irrigation_system_id', null);
			formik.setFieldError('irrigation_system_id', undefined); // Clear the error
			formik.setFieldTouched('irrigation_system_id', false); // Reset touched state
		}
	};

	return (
		<>
			<form onSubmit={formik.handleSubmit}>
				<div className="flex flex-col items-center justify-center">
					<div className="w-full xs:w-2/3 mb-[20px] flex flex-col  justify-center items-center ">
						<h6 className="text-dark text-center font-bold text-xl leading-normal mb-[20px]">
							{t('myFarm.locationLabel')}
						</h6>

						{loading ? (
							<Skeleton
								variant="rectangular"
								width="100%"
								sx={{
									borderRadius: '8px',
								}}
								height={42}
							/>
						) : (
							<TaInput
								click={openMapsModal}
								endIcon={<FmdGoodOutlinedIcon />}
								id="crop_location_name"
								readonly={true}
								label={t('myFarm.farmLocation')}
								size="small"
								value={formik.values.crop_location_name as string}
								onChange={formik.handleChange}
								error={formik.errors.crop_location_name}
								touched={formik.touched.crop_location_name}
							/>
						)}
					</div>
					<div className="flex flex-col md:flex-row  sm:mt-[40px] mt-0">
						<div className="w-full md:w-1/2 ">
							{/*{checkboxFields.slice(0, 4).map(({ label, name }) => (*/}
							{checkboxFields.slice(0, 4).map(({ label, name }) => (
								<div key={name}>
									<FormControlLabel
										disabled={loading}
										control={
											<Checkbox
												checked={!!formik.values[name]}
												onChange={handleCheckboxChange}
												name={name}
												sx={{ paddingTop: 0 }}
											/>
										}
										sx={{ display: 'flex', alignItems: 'flex-start' }}
										label={
											<span className="text-secondary font-roboto text-base !font-normal leading-normal">
												{t(`myFarm.${label}`)}
											</span>
										}
									/>
								</div>
							))}
							{formik.values.irrigation_system && (
								<div className="w-full  md:pr-[10px] my-[20px] sm:my-[8px] mx-0 sm:mx-[33px]">
									<TaAutocomplete
										options={irrigation}
										getOptionLabel={(option) => option.name}
										label={t('myFarm.irrigation_system_id')}
										value={formik.values.irrigation_system_id || null}
										onChange={(_, value) => formik.setFieldValue('irrigation_system_id', value)}
										error={Boolean(
											formik.touched.irrigation_system_id && formik.errors.irrigation_system_id
										)}
									/>
									{formik.touched.irrigation_system_id && formik.errors.irrigation_system_id && (
										<ErrorMessage
											message={t(formik.errors.irrigation_system_id as string, {
												fieldName: t('irrigation_system_id'),
											})}
										/>
									)}
								</div>
							)}
						</div>

						<div className="w-full md:w-1/2 ">
							{checkboxFields.slice(4).map(({ label, name }) => (
								<div key={name}>
									<FormControlLabel
										disabled={loading}
										control={
											<Checkbox
												checked={!!formik.values[name]}
												onChange={formik.handleChange}
												name={name}
												sx={{ paddingTop: 0 }}
											/>
										}
										sx={{ display: 'flex', alignItems: 'flex-start' }}
										label={
											<span className="text-secondary font-roboto text-base !font-normal leading-normal">
												{t(`myFarm.${label}`)}
											</span>
										}
									/>
								</div>
							))}
							{formik.values.soil_analysis && (
								<>
									<Button
										startIcon={<AttachFileIcon />}
										component="label"
										className="ml-[23px] sm:ml-0"
										color="primary"
										role={undefined}
										variant="text"
										tabIndex={-1}
									>
										<span className="text-dark font-roboto text-base font-bold underline normal-case">
											{t('myFarm.attach_soil_analysis')}
										</span>
										<VisuallyHiddenInput
											accept=".txt,.doc,.docx,.pdf,.xls,.xlsx,.ppt,.kmz,.kml"
											type="file"
											onChange={handleFileChange}
											multiple
										/>
									</Button>

									<div className="flex flex-col gap-[10px] mb-[10px]">
										{!isAllCompleted &&
											uploadStatus.map((status, index) => (
												<FileUploadIndicator
													completed={status.isCompleted}
													error={Boolean(status.error)}
													key={index}
													label={status.fileName}
													progress={status.progress}
												/>
											))}
									</div>

									<div className="flex flex-col gap-[10px]">
										{singleFarm?.farm_files?.map((file: any) => (
											<CustomTooltip
												open={true}
												key={file.id}
												marginLeftArrow={true}
												placement="top"
												title={
													<Box className="flex items-center gap-[10px] text-blue">
														<Typography>{t('cvUploadTooltipText')}</Typography>
													</Box>
												}
											>
												<FileUploadIndicator
													completed={true}
													error={false}
													key={file.id}
													label={file.filename}
													removeFile={() => handleFileRemove(file.id)}
												/>
											</CustomTooltip>
										))}
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</form>

			<GeneralMuiModal
				maxWidth={'xl'}
				title={t('myFarm.farmLocation')}
				styles={{
					paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },
					contentStyle: { padding: 0 },
				}}
				open={mapOpen}
				onClose={closeMapsModal}
				content={
					<>
						<TaMaps
							onSave={() => {
								return setMapOpen(false);
							}}
							currentAddress={formik.values.crop_location_name}
							marker={{
								lat: Number(formik.values.crop_latitude),
								lng: Number(formik.values.crop_longitude),
							}}
							onLocationSelect={handleLocationSelect}
						/>
					</>
				}
			/>
		</>
	);
}
